/**
 * Realtime event name constants & payload types.
 * See specs/006-/data-model.md (即時事件 Payload) and contracts/realtime-events.md.
 *
 * `RECONNECTED` is not sent by the server — useTripRealtime dispatches it
 * locally after a successful reconnect so subscribers can trigger a full
 * snapshot refetch (research.md #4 / T039).
 */

export const REALTIME_EVENTS = {
  MEMBER_JOINED: 'MEMBER_JOINED',
  MEMBER_ROLE_CHANGED: 'MEMBER_ROLE_CHANGED',
  MEMBER_REMOVED: 'MEMBER_REMOVED',
  POLL_CREATED: 'POLL_CREATED',
  POLL_VOTED: 'POLL_VOTED',
  POLL_VOTE_REVOKED: 'POLL_VOTE_REVOKED',
  POLL_CLOSED: 'POLL_CLOSED',
  ITINERARY_ADDED: 'ITINERARY_ADDED',
  ITINERARY_UPDATED: 'ITINERARY_UPDATED',
  ITINERARY_MOVED: 'ITINERARY_MOVED',
  ITINERARY_DELETED: 'ITINERARY_DELETED',
  RECONNECTED: 'RECONNECTED',
} as const;

export type RealtimeEventName = (typeof REALTIME_EVENTS)[keyof typeof REALTIME_EVENTS];

export interface MemberJoinedPayload {
  tripId: number | string;
  userId: number;
}

export interface MemberRoleChangedPayload {
  tripId: number | string;
  userId: number;
  role: 'OWNER' | 'EDITOR';
}

export interface MemberRemovedPayload {
  tripId: number | string;
  userId: number;
}

export interface PollCreatedPayload {
  tripId: number | string;
  pollId: number | string;
  subject: string;
}

export interface PollVotedPayload {
  tripId: number | string;
  pollId: number | string;
  optionId: number | string;
  userId: number;
}

export interface PollVoteRevokedPayload {
  tripId: number | string;
  pollId: number | string;
  optionId: number | string;
  userId: number;
}

export interface PollClosedPayload {
  tripId: number | string;
  pollId: number | string;
}

export interface ItineraryAddedPayload {
  tripId: number | string;
  itineraryId: string;
  title: string;
  location?: string;
}

export interface ItineraryUpdatedPayload {
  tripId: number | string;
  itineraryId: string;
  title?: string;
  startTime?: string | null;
  endTime?: string | null;
  location?: string;
  note?: string;
  type?: string;
}

export interface ItineraryMovedPayload {
  tripId: number | string;
  itineraryId: string;
  daySequence: number | null;
  orderInDay: number | null;
}

export interface ItineraryDeletedPayload {
  tripId: number | string;
  itineraryId: string;
}

export interface RealtimeEventPayloadMap {
  MEMBER_JOINED: MemberJoinedPayload;
  MEMBER_ROLE_CHANGED: MemberRoleChangedPayload;
  MEMBER_REMOVED: MemberRemovedPayload;
  POLL_CREATED: PollCreatedPayload;
  POLL_VOTED: PollVotedPayload;
  POLL_VOTE_REVOKED: PollVoteRevokedPayload;
  POLL_CLOSED: PollClosedPayload;
  ITINERARY_ADDED: ItineraryAddedPayload;
  ITINERARY_UPDATED: ItineraryUpdatedPayload;
  ITINERARY_MOVED: ItineraryMovedPayload;
  ITINERARY_DELETED: ItineraryDeletedPayload;
  RECONNECTED: Record<string, never>;
}
